import React from 'react';
import Axios from 'axios';
import { useMutation } from '@tanstack/react-query';
import * as Routes from '@/modules/routes';
import SPAButton from '@/components/admin/SysadminSPA/shared/SPAButton';
import SPADialog from '@/components/admin/SysadminSPA/shared/SPADialog';
import { useSPAToast } from '@/components/admin/SysadminSPA/shared/SPAToast';
import type { SlideRow } from './types';

export interface CopiedSlide {
  id: number;
  position: number;
  title: string | null;
}

interface CopySlideDialogProps {
  onCopied: (source: SlideRow, copy: CopiedSlide) => void;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  slide: SlideRow;
}

// Copies through the plato_api endpoint the builder itself uses, so the row can be added without a reload.
const CopySlideDialog = ({
  onCopied, onOpenChange, open, slide,
}: CopySlideDialogProps) => {
  const spaToast = useSPAToast();
  const copy = useMutation({
    // The copy endpoint renders the slide bare, without the usual `data` root. The title matches what
    // the old server-side clone produced.
    mutationFn: () => Axios
      .post(Routes.plato_api_copy_slide_path(slide.id), { title: `${slide.title} (copy)` })
      .then(res => res.data as CopiedSlide),
    onSuccess: (copied) => {
      onOpenChange(false);
      onCopied(slide, copied);
      spaToast.success(`Copied ${slide.title}.`);
    },
  });

  const handleOpenChange = (next: boolean) => {
    if (!next) copy.reset();
    onOpenChange(next);
  };

  return (
    <SPADialog open={open} onOpenChange={handleOpenChange}>
      <SPADialog.Content size="sm">
        <SPADialog.Header>
          <SPADialog.Title>Copy {slide.title}?</SPADialog.Title>
          <SPADialog.CloseButton />
        </SPADialog.Header>
        <SPADialog.Body className="tw-flex tw-flex-col tw-gap-3">
          <SPADialog.Description>{slide.title} (copy) is added right after this slide.</SPADialog.Description>
          {copy.isError && (
            <p className="tw-m-0 tw-text-sm tw-text-spa-error" role="alert">The slide could not be copied. Try again.</p>
          )}
        </SPADialog.Body>
        <SPADialog.Footer>
          <SPADialog.Close asChild>
            <SPAButton>Cancel</SPAButton>
          </SPADialog.Close>
          <SPAButton disabled={copy.isLoading} variant="primary" onClick={() => copy.mutate()}>Copy</SPAButton>
        </SPADialog.Footer>
      </SPADialog.Content>
    </SPADialog>
  );
};

export default CopySlideDialog;
